// ============================================================================
// ResetPassword — /reset-password?token=XXX
// Landed from the "Reset your password" email link.
// ============================================================================
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, Stack,
  alpha, useTheme, CircularProgress,
} from '@mui/material';
import { LockReset, AutoAwesome, CheckCircle } from '@mui/icons-material';
import { authApi } from '../services/apiService';

export default function ResetPassword() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${alpha('#1a3a6e', 0.08)}, ${alpha('#e8722a', 0.05)})`,
      p: 2,
    }}>
      <Card elevation={0} sx={{
        width: '100%', maxWidth: 420, borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2.5,
              background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AutoAwesome sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>The Cycle</Typography>
              <Typography variant="caption" color="text.secondary">Reset your password</Typography>
            </Box>
          </Box>

          {!token ? (
            <Alert severity="error">
              Invalid reset link. <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/')}>Return home</Box>
            </Alert>
          ) : done ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
              <CheckCircle sx={{ fontSize: 52, color: 'success.main' }} />
              <Typography variant="h6" fontWeight={700} textAlign="center">Password updated!</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Your password has been changed. You can now sign in with your new password.
              </Typography>
              <Button variant="contained" fullWidth sx={{ fontWeight: 700, borderRadius: 2 }} onClick={() => navigate('/')}>
                Go to The Cycle
              </Button>
            </Stack>
          ) : (
            <form onSubmit={submit}>
              <Stack spacing={2}>
                {error && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.83rem' }}>{error}</Alert>}
                <TextField
                  label="New password" type="password" size="small" fullWidth required
                  value={password} onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password" helperText="At least 8 characters"
                />
                <TextField
                  label="Confirm new password" type="password" size="small" fullWidth required
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  type="submit" variant="contained" fullWidth disabled={loading}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockReset />}
                >
                  {loading ? 'Saving…' : 'Set new password'}
                </Button>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
